import type { FC } from 'react';
import { Suspense } from 'react';
import type { MetricField } from '../../types';
import { observer } from 'mobx-react-lite';
import useCurrentTasksFormStore from '../../hooks/useCurrentTasksFormStore';
import { Box, Tooltip, Typography } from '@mui/material';
import { SuspenseFallbackSkeleton } from '../RuleEditTasksFormComponents';
import ConditionMultiplayer from './ConditionMultiplayer';
import ConditionMetrics from './ConditionMetrics';
import ConstantsMetrics from './ConstantsMetrics';
import PeriodsMetrics from './PeriodsMetrics';
import LevelsMetrics from './LevelsMetrics';
import FlowsMetrics from './FlowsMetrics';
import { FLOW_CONSTANT_METRICS } from '../../constants';

interface CommonProps {
  indexTask: number;
  indexCondition: number;
  field: MetricField;
  title: string;
  isSecond?: boolean; // for second metric condition
}

interface ConditionType extends CommonProps {
  indexVariant: undefined;
}

interface VariantType extends CommonProps {
  indexVariant: number;
}

type IProps = ConditionType | VariantType;

const ConditionForm: FC<IProps> = observer(({ title, indexCondition, indexTask, field, isSecond, indexVariant }) => {
  const tasksFormStore = useCurrentTasksFormStore();

  const fieldValue = tasksFormStore.getConditionMetric(indexTask, indexCondition)?.[field];
  const isConstantField = FLOW_CONSTANT_METRICS.includes(fieldValue as string);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
        }}
      >
        <Box sx={{ flex: 1, flexDirection: 'column' }}>
          <Box sx={{ flex: 1, height: 24, maxHeight: 24, pl: 1.2 }}>
            <Typography variant="body2" component="span" sx={(t) => ({ color: t.palette.primary.main })}>
              {title}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="Multiplier" placement="top" arrow>
              <Box
                sx={(t) => ({
                  flex: '0 0 62px',
                  border: `1px solid ${t.palette.divider}`,
                  borderRight: 'none',
                  borderRadius: `${t.borderRadius.md} 0 0 ${t.borderRadius.md}`,
                  height: 36,
                  maxHeight: 36,
                  width: '100%',
                })}
              >
                <ConditionMultiplayer
                  indexCondition={indexCondition}
                  indexTask={indexTask}
                  field={isSecond ? 'second_metric_multiplier' : 'metric_multiplier'}
                  indexVariant={indexVariant}
                />
              </Box>
            </Tooltip>
            <Box
              sx={(t) => ({
                flex: 1,
                border: `1px solid ${t.palette.divider}`,
                borderRight: isConstantField ? 'none' : '',
                height: 36,
                maxHeight: 36,
              })}
            >
              <Suspense fallback={<SuspenseFallbackSkeleton />}>
                <ConditionMetrics
                  indexCondition={indexCondition}
                  indexTask={indexTask}
                  field={field}
                  indexVariant={indexVariant}
                />
              </Suspense>
            </Box>
          </Box>
        </Box>
        {isConstantField ? (
          <Box sx={{ flex: 1, flexDirection: 'column' }}>
            <Box sx={{ flex: 1, height: 24, maxHeight: 24, pl: 2 }}>
              <Typography
                variant="body2"
                component="span"
                sx={(t) => ({ textTransform: 'capitalize', color: t.palette.text.secondary })}
              >
                Constant Name
              </Typography>
            </Box>
            <Box
              sx={(t) => ({
                flex: 1,
                border: `1px solid ${t.palette.divider}`,
                borderRadius: `0 ${t.borderRadius.md} ${t.borderRadius.md} 0`,
                height: 36,
              })}
            >
              <Suspense fallback={<SuspenseFallbackSkeleton />}>
                <>
                  {fieldValue === 'constant' && (
                    <ConstantsMetrics
                      indexCondition={indexCondition}
                      indexTask={indexTask}
                      field={isSecond ? 'second_metric_constant_name' : 'constant_name'}
                      indexVariant={indexVariant}
                    />
                  )}
                  {fieldValue === 'flow' && (
                    <FlowsMetrics
                      indexCondition={indexCondition}
                      indexTask={indexTask}
                      field={isSecond ? 'second_metric_constant_name' : 'constant_name'}
                      indexVariant={indexVariant}
                    />
                  )}
                </>
              </Suspense>
            </Box>
          </Box>
        ) : null}
        {!isConstantField ? (
          <>
            <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column' }}>
              <Box sx={{ flex: 1, height: 24, maxHeight: 24, pl: 2 }}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={(t) => ({ textTransform: 'capitalize', color: t.palette.text.secondary })}
                >
                  Level
                </Typography>
              </Box>
              <Box
                sx={(t) => ({
                  flex: 1,
                  borderTop: `1px solid ${t.palette.divider}`,
                  borderBottom: `1px solid ${t.palette.divider}`,
                  height: 36,
                  maxHeight: 36,
                })}
              >
                <Suspense fallback={<SuspenseFallbackSkeleton />}>
                  <LevelsMetrics
                    indexCondition={indexCondition}
                    indexTask={indexTask}
                    field={isSecond ? 'second_metric_level' : 'metric_level'}
                    indexVariant={indexVariant}
                  />
                </Suspense>
              </Box>
            </Box>
            <Box sx={{ flex: 1, flexDirection: 'column' }}>
              <Box sx={{ flex: 1, height: 24, maxHeight: 24, pl: 2 }}>
                <Typography
                  variant="body2"
                  component="span"
                  sx={(t) => ({ textTransform: 'capitalize', color: t.palette.text.secondary })}
                >
                  Period
                </Typography>
              </Box>
              <Box
                sx={(t) => ({
                  flex: 1,
                  border: `1px solid ${t.palette.divider}`,
                  borderRadius: `0 ${t.borderRadius.md} ${t.borderRadius.md} 0`,
                  height: 36,
                  maxHeight: 36,
                })}
              >
                <Suspense fallback={<SuspenseFallbackSkeleton />}>
                  <PeriodsMetrics
                    indexCondition={indexCondition}
                    indexTask={indexTask}
                    field={isSecond ? 'second_period' : 'period'}
                    indexVariant={indexVariant}
                  />
                </Suspense>
              </Box>
            </Box>
          </>
        ) : null}
      </Box>
    </Box>
  );
});

export default ConditionForm;
